import type { GlobalConfiguration } from '../models/GlobalConfiguration';

export type PushNotificationConfigField = keyof Pick<
  GlobalConfiguration,
  | 'enablePushEoiSubmitted'
  | 'enablePushEoiUpdated'
  | 'enablePushEoiSuggested'
  | 'enablePushEoiWithdrawn'
  | 'enablePushReferralOpportunityShared'
  | 'enablePushPromotionImmediate'
  | 'enablePushPromotionDelayed'
  | 'enablePushFundProgressUpdate'
  | 'enablePushFundCashflowReport'
  | 'enablePushContactDeeplink'
  | 'enablePushChatMessage'
>;

export type PushNotificationTypeDefinition = {
  field: PushNotificationConfigField;
  label: string;
  description: string;
  /** Matches `notificationType` sent to PushNotificationService */
  notificationType: string;
};

export type PushNotificationCategory = {
  id: string;
  title: string;
  description: string;
  types: PushNotificationTypeDefinition[];
};

/** Admin UI + global config: categorized mobile push types investors/admins can receive. */
export const PUSH_NOTIFICATION_CATEGORIES: PushNotificationCategory[] = [
  {
    id: 'eoi',
    title: 'Expression of interest (EOI)',
    description:
      'Sent to the investor when an advisor submits, updates, suggests, or withdraws an EOI on their behalf.',
    types: [
      {
        field: 'enablePushEoiSubmitted',
        notificationType: 'eoi_submitted',
        label: 'EOI submitted',
        description: 'Advisor submits a new EOI for the investor.',
      },
      {
        field: 'enablePushEoiUpdated',
        notificationType: 'eoi_updated',
        label: 'EOI updated',
        description: 'Advisor changes an existing EOI (amount, entity, or other details).',
      },
      {
        field: 'enablePushEoiSuggested',
        notificationType: 'eoi_suggested',
        label: 'EOI suggested',
        description: 'Advisor suggests an EOI for the investor to review or accept.',
      },
      {
        field: 'enablePushEoiWithdrawn',
        notificationType: 'eoi_withdrawn',
        label: 'EOI withdrawn',
        description: 'Advisor withdraws an EOI on behalf of the investor.',
      },
    ],
  },
  {
    id: 'referrals',
    title: 'Referrals',
    description: 'Sent when someone shares a fund opportunity with the investor via referral.',
    types: [
      {
        field: 'enablePushReferralOpportunityShared',
        notificationType: 'referral_opportunity_shared',
        label: 'Opportunity shared',
        description: 'A referral invitation or shared opportunity is available in the app.',
      },
    ],
  },
  {
    id: 'promotions',
    title: 'Investor promotions',
    description:
      'Sent when an investor is promoted to lead on a fund. Delayed promotions respect the minimum delay configured below.',
    types: [
      {
        field: 'enablePushPromotionImmediate',
        notificationType: 'promotion_immediate',
        label: 'Immediate promotion',
        description: 'Notify as soon as the investor is promoted to lead (no minimum delay).',
      },
      {
        field: 'enablePushPromotionDelayed',
        notificationType: 'promotion_delayed',
        label: 'Delayed promotion',
        description:
          'Notify after the promotion delay elapses (also used for legacy delayed_promotion events).',
      },
    ],
  },
  {
    id: 'fund-updates',
    title: 'Fund updates',
    description: 'Sent when new fund content is published to investors who hold that investment.',
    types: [
      {
        field: 'enablePushFundProgressUpdate',
        notificationType: 'progress_update',
        label: 'Progress update',
        description: 'A fund progress or milestone update is published.',
      },
      {
        field: 'enablePushFundCashflowReport',
        notificationType: 'cashflow_report',
        label: 'Cashflow report',
        description: 'A fund cashflow report is published.',
      },
    ],
  },
  {
    id: 'chat',
    title: 'Fides team chat',
    description: 'Sent to the investor when the Fides team replies in in-app chat.',
    types: [
      {
        field: 'enablePushChatMessage',
        notificationType: 'chat_message',
        label: 'New chat message',
        description: "Admin sends a message in the investor's Fides chat conversation.",
      },
    ],
  },
  {
    id: 'admin',
    title: 'Admin utilities',
    description: 'Operational pushes initiated from the admin web app (not fund or EOI workflow).',
    types: [
      {
        field: 'enablePushContactDeeplink',
        notificationType: 'contact_deeplink',
        label: 'Open in app (contact)',
        description:
          'Admin uses "Open in App" on a contact — sends a deep link to that contact\'s registered devices.',
      },
    ],
  },
];

export const ALL_PUSH_NOTIFICATION_FIELDS: PushNotificationConfigField[] =
  PUSH_NOTIFICATION_CATEGORIES.flatMap((category) => category.types.map((t) => t.field));

export function isAnyPushTypeEnabled(
  cfg: Pick<GlobalConfiguration, PushNotificationConfigField>,
): boolean {
  return ALL_PUSH_NOTIFICATION_FIELDS.some((field) => !!cfg[field]);
}
